// Copyright © 2019 Atomic.io Limited. All rights reserved.
import {useEffect, useRef, useState} from 'react'
import {CloseIcon} from './icons'

const MODAL_STREAM_SELECTOR = '.atomic-sdk-frame.modal-stream'

const isModalOpen = () => {
  const dialog = document.querySelector(MODAL_STREAM_SELECTOR)
  return Boolean(dialog && !dialog.classList.contains('closed'))
}

// Force exit out of the modal variant: clicking it resets the active stream container to the
// standalone variant (handled by the supplied `onClose`).
const ModalStreamCloseButton = ({onClose}) => {
  const [open, setOpen] = useState(false)
  const buttonRef = useRef(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    const sync = () => setOpen(isModalOpen())

    sync()

    const observer = new MutationObserver(sync)
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      // `class` carries the dialog's open/closed state; keep `aria-hidden` too as
      // the SDK toggles it alongside.
      attributeFilter: ['class', 'aria-hidden'],
    })

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const button = buttonRef.current

    if (!button) {
      return undefined
    }

    const handleActivate = () => {
      setOpen(false)
      onCloseRef.current()
    }

    button.addEventListener('click', handleActivate)

    return () => button.removeEventListener('click', handleActivate)
  }, [open])

  if (!open) {
    // The modal stream container is not open, so we do not need to render the
    // close button.
    return null
  }

  return (
    <button
      type="button"
      ref={buttonRef}
      className="modal-stream-close"
      aria-label="Exit modal stream container"
      title="Exit modal stream container"
    >
      <CloseIcon />
    </button>
  )
}

export {ModalStreamCloseButton}
